import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Icon } from "@iconify/react";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  DragOverlay,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
} from "@dnd-kit/core";
import { ReportHeader } from "./ReportHeader";
import { SchemaBrowser } from "./SchemaBrowser";
import { ReportCanvas, SimpleColumnRow } from "./ReportCanvas";
import { ColumnCard } from "./ColumnCard";
import { FiltersPanel } from "./FiltersPanel";
import { ColumnEditor } from "./ColumnEditor";
import { SheetTabs } from "./SheetTabs";
import { useReportBuilderStore } from "./store";
import { ReportConfig, SchemaColumn, SchemaTable } from "./types";
import {
  fetchReportSchema,
  getCustomReport,
} from "../../../api/customReportApi";
import {
  HStack,
  PrimaryCTAButton,
  TertiaryCTAButton,
  VStack,
} from "../../../components/utils";

// ─── Help panel ───────────────────────────────────────────────────────────────

const HELP_ITEMS = [
  {
    icon: "mdi:table-column-plus-after",
    title: "Add columns",
    detail:
      "Browse the Schema panel on the left. Click + or drag a column onto the canvas to add it to the active sheet.",
  },
  {
    icon: "mdi:swap-horizontal",
    title: "Reorder columns",
    detail:
      "Drag column cards left or right on the canvas to change the order they appear in the exported file.",
  },
  {
    icon: "mdi:pencil",
    title: "Rename a sheet",
    detail:
      "Double-click any sheet tab at the top to rename it. The name is used as the Excel tab label.",
  },
  {
    icon: "mdi:table-plus",
    title: "Multiple sheets",
    detail:
      "Click + next to the sheet tabs to add another sheet. Each sheet has its own columns, filters, grain and sort settings.",
  },
  {
    icon: "mdi:filter-variant",
    title: "Filter rows",
    detail:
      "Open the Filters panel to add conditions. Multiple filter groups are joined with AND; rules inside a group can be AND or OR.",
  },
  {
    icon: "mdi:chart-bar",
    title: "Report basis (grain)",
    detail:
      "The 'Report basis' dropdown on the tab bar controls what one row represents: Grant, Employee, Plan, Vesting Tranche, or Transaction.",
  },
  {
    icon: "mdi:swap-vertical",
    title: "Sort & group",
    detail:
      "Use the Sort and Group By options on each column card to control row ordering and aggregation.",
  },
  {
    icon: "mdi:function-variant",
    title: "Custom formula columns",
    detail:
      "Enable Advanced mode (toggle in the header) and click 'Custom column' to add a formula column. Use {column_name} to reference other columns.",
  },
  {
    icon: "mdi:shield-account-outline",
    title: "Mask PII data",
    detail:
      "Turn on 'Mask PII Data' in report settings to redact name, email, PAN and contact fields in the generated output.",
  },
  {
    icon: "mdi:content-save",
    title: "Save & generate",
    detail:
      "Save the report first (Ctrl+S or the Save button), then use Export > Generate Report to queue it. Come back to download once it is ready.",
  },
  {
    icon: "mdi:calendar-clock",
    title: "Schedule",
    detail:
      "In the Export menu you can schedule the report to run on a recurring interval or a fixed day each month, and deliver it to selected recipients.",
  },
];

function HelpPanel({
  onClose,
  onStartTour,
}: {
  onClose: () => void;
  onStartTour: () => void;
}) {
  const [search, setSearch] = useState("");
  const filtered = HELP_ITEMS.filter(
    (h) =>
      h.title.toLowerCase().includes(search.toLowerCase()) ||
      h.detail.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="absolute right-0 top-10 z-50 w-96 bg-white border border-borderColor rounded-xl shadow-xl overflow-hidden flex flex-col max-h-[80vh]">
      <div className="flex items-center justify-between px-4 py-3 border-b border-borderColor">
        <span className="text-xs font-semibold text-secondary">
          How to use the Custom Report Builder
        </span>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
          <Icon icon="mdi:close" width={16} />
        </button>
      </div>
      <div className="px-3 py-2 border-b border-borderColor">
        <button
          onClick={onStartTour}
          className="mb-2 flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
        >
          <Icon icon="mdi:map-marker-path" width={15} />
          Take a 60-second tour
        </button>
        <input
          autoFocus
          type="text"
          placeholder="Search help..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full text-xs px-2.5 py-1.5 border border-inputBorder rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </div>
      <div className="overflow-y-auto flex-1">
        {filtered.map((item) => (
          <div
            key={item.title}
            className="flex items-start gap-3 px-4 py-3 border-b border-borderColor last:border-0 hover:bg-tertiaryBg transition-colors"
          >
            <Icon
              icon={item.icon}
              width={18}
              className="text-primary flex-shrink-0 mt-0.5"
            />
            <div>
              <p className="text-xs font-semibold text-primaryText">
                {item.title}
              </p>
              <p className="text-[11px] text-secondaryText mt-0.5 leading-snug">
                {item.detail}
              </p>
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <p className="px-4 py-6 text-xs text-descriptionColor text-center">
            No help items match your search.
          </p>
        )}
      </div>
    </div>
  );
}

function ContextualHelpPrompt({
  onStartTour,
  onDismiss,
}: {
  onStartTour: () => void;
  onDismiss: () => void;
}) {
  return (
    <aside
      aria-label="Report builder help"
      className="fixed bottom-6 right-6 z-50 w-[calc(100%-32px)] max-w-sm rounded-lg border border-borderColor bg-white p-4 shadow-card"
    >
      <HStack className="items-start gap-3">
        <Icon
          aria-hidden="true"
          icon="mdi:help-circle-outline"
          width={20}
          className="mt-0.5 shrink-0 text-primary"
        />
        <VStack className="min-w-0 gap-2">
          <VStack className="gap-1">
            <h2 className="text-base font-semibold leading-6 text-headerColor">
              Need help creating this report?
            </h2>
            <p className="text-sm leading-5 text-secondaryText">
              Take a quick tour, or choose a starter report from the Help menu.
            </p>
          </VStack>
          <HStack className="flex-wrap items-center gap-2">
            <TertiaryCTAButton onClick={onDismiss}>Not now</TertiaryCTAButton>
            <PrimaryCTAButton onClick={onStartTour}>
              Start quick tour
            </PrimaryCTAButton>
          </HStack>
        </VStack>
      </HStack>
    </aside>
  );
}

const REPORT_STARTERS = [
  {
    title: "Employee directory",
    detail: "Employee details and compensation fields.",
    grain: "employee" as const,
    fields: [
      "employee_name",
      "employee_id",
      "email",
      "designation",
      "employee_salary",
    ],
  },
  {
    title: "Outstanding grants",
    detail: "Granted, vested, and outstanding options by employee.",
    grain: "grant" as const,
    fields: [
      "employee_name",
      "plan_name",
      "grant_date",
      "granted_options",
      "vested",
      "outstanding",
    ],
  },
  {
    title: "Upcoming vesting",
    detail: "Upcoming vesting events and option quantities.",
    grain: "vesting_tranche" as const,
    fields: [
      "employee_name",
      "plan_name",
      "vesting_date",
      "options_in_tranche",
      "vesting_state",
    ],
  },
  {
    title: "Grant transactions",
    detail: "Exercise, cash-out, and other grant transactions.",
    grain: "transaction" as const,
    fields: [
      "employee_name",
      "plan_name",
      "transaction_date",
      "transaction_type",
      "transaction_options",
    ],
  },
];

const BUILDER_TOUR_STEPS = [
  {
    element: "[data-tour='report-basis']",
    popover: {
      title: "Choose the report basis",
      description:
        "This decides what one row represents. Start with grants for option details or employees for a people-level view.",
      side: "bottom" as const,
      align: "end" as const,
    },
  },
  {
    element: "[data-tour='report-fields']",
    popover: {
      title: "Add or remove fields",
      description:
        "Search for the information you need, then add it to the report. Filters can use only fields included here.",
      side: "right" as const,
      align: "start" as const,
    },
  },
  {
    element: "[data-tour='report-preview']",
    popover: {
      title: "Check a small live sample",
      description:
        "This preview uses up to 10 records. It is a quick check of the columns, filters, and formulas before you save.",
      side: "top" as const,
      align: "end" as const,
    },
  },
  {
    element: "[data-tour='save-report']",
    popover: {
      title: "Name and save",
      description:
        "Give the report a clear name, save it, then generate or download it when you are ready.",
      side: "bottom" as const,
      align: "end" as const,
    },
  },
];

function createStarterConfig(
  starter: typeof REPORT_STARTERS[number],
  schema: SchemaTable[]
): ReportConfig {
  const fields = schema.flatMap((table) => table.columns);
  const columns = starter.fields
    .map((field) => fields.find((column) => column.name === field))
    .filter((column): column is SchemaColumn => Boolean(column))
    .map((column, index) => ({
      id: `starter_${index}_${column.id}`,
      sourceTable: column.sourceTable,
      sourceColumn: column.name,
      displayName: column.displayName,
      type: column.dataType,
      isCustom: false,
      description: column.description,
      format: null,
      aggregation: null,
    }));

  return {
    name: starter.title,
    description: starter.detail,
    sheets: [
      {
        id: "starter_sheet",
        name: starter.title,
        reportGrain: starter.grain,
        columns,
        filters: [],
        groupBy: [],
        sortBy: [],
      },
    ],
    settings: {
      visibility: "private",
      refreshSchedule: "none",
      maskPii: false,
    },
  };
}

function StarterScreen({
  schema,
  onSelect,
  onStartBlank,
}: {
  schema: SchemaTable[];
  onSelect: (starter: typeof REPORT_STARTERS[number]) => void;
  onStartBlank: () => void;
}) {
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-4xl">
        <div className="text-center mb-8">
          <p className="text-sm font-semibold text-primary">Custom reports</p>
          <h1 className="mt-2 text-2xl font-semibold text-primaryText">
            Start with a useful report
          </h1>
          <p className="mt-2 text-sm text-secondaryText">
            Choose a starting point. You can add fields, refine it, and preview
            up to 10 representative records before saving.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {REPORT_STARTERS.map((starter) => (
            <button
              key={starter.title}
              onClick={() => onSelect(starter)}
              disabled={schema.length === 0}
              className="text-left bg-white rounded-xl border border-gray-200 p-5 hover:border-primary hover:shadow-sm transition-all disabled:opacity-60"
            >
              <Icon
                icon="mdi:file-chart-outline"
                width={22}
                className="text-primary"
              />
              <p className="mt-3 text-sm font-semibold text-primaryText">
                {starter.title}
              </p>
              <p className="mt-1 text-xs leading-5 text-secondaryText">
                {starter.detail}
              </p>
            </button>
          ))}
        </div>
        <div className="mt-5 text-center">
          <button
            onClick={onStartBlank}
            className="text-xs font-medium text-secondaryText hover:text-primary underline underline-offset-4"
          >
            Build from scratch
          </button>
        </div>
      </div>
    </div>
  );
}

export function CustomReportBuilder() {
  const { id } = useParams<{ id?: string }>();
  const {
    undo,
    redo,
    setSchema,
    loadReport,
    resetReport,
    addColumn,
    reorderColumns,
    config,
    activeSheetId,
    isAdvancedMode,
    schema,
  } = useReportBuilderStore();
  const [loading, setLoading] = useState(true);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showStarters, setShowStarters] = useState(!id);
  const [showContextualHelp, setShowContextualHelp] = useState(false);
  const [helpActivityVersion, setHelpActivityVersion] = useState(0);
  const [hasDismissedContextualHelp, setHasDismissedContextualHelp] = useState(
    () =>
      window.sessionStorage.getItem(
        "custom-report-builder-contextual-help-v1"
      ) === "dismissed"
  );

  // ── Shared DndContext state ──────────────────────────────────────────────────
  const [isDragOver, setIsDragOver] = useState(false);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [activeDragData, setActiveDragData] = useState<any>(null);

  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    })
  );

  const activeSheet =
    config.sheets.find((s) => s.id === activeSheetId) ?? config.sheets[0];
  const allColumnIds = new Set(activeSheet.columns.map((c) => c.id));
  // The canvas column card being dragged (for reorder overlay)
  const activeDragCol = activeSheet.columns.find((c) => c.id === activeDragId);
  const isBlankReport =
    !id &&
    !showStarters &&
    !isReadOnly &&
    config.sheets.every((sheet) => sheet.columns.length === 0);

  function markMeaningfulActivity(event: React.SyntheticEvent) {
    const target = event.target as HTMLElement;
    if (
      target.closest(
        "button, input, textarea, select, [role='button'], [draggable='true']"
      )
    ) {
      setShowContextualHelp(false);
      setHelpActivityVersion((version) => version + 1);
    }
  }

  function dismissContextualHelp() {
    window.sessionStorage.setItem(
      "custom-report-builder-contextual-help-v1",
      "dismissed"
    );
    setHasDismissedContextualHelp(true);
    setShowContextualHelp(false);
  }

  function startTour() {
    setShowHelp(false);
    dismissContextualHelp();
    window.setTimeout(() => {
      const reportTour = driver({
        steps: BUILDER_TOUR_STEPS,
        showProgress: true,
        progressText: "{{current}} of {{total}}",
        showButtons: ["previous", "next", "close"],
        nextBtnText: "Next",
        prevBtnText: "Back",
        doneBtnText: "Done",
        overlayColor: "#1f2937",
        overlayOpacity: 0.45,
        stagePadding: 6,
        stageRadius: 6,
        popoverClass: "hissa-product-tour",
        skipMissingElement: true,
        onDestroyed: () => {
          window.localStorage.setItem(
            "custom-report-builder-guide-v1",
            "dismissed"
          );
        },
      });
      reportTour.drive();
    }, 0);
  }

  function handleDragStart(event: DragStartEvent) {
    setActiveDragId(String(event.active.id));
    setActiveDragData(event.active.data?.current ?? null);
  }

  function handleDragOver(event: DragOverEvent) {
    if (event.active.data?.current?.type === "schema-column") {
      setIsDragOver(true);
    }
  }

  function handleDragEnd(event: DragEndEvent) {
    setIsDragOver(false);
    setActiveDragId(null);
    setActiveDragData(null);
    const { active, over } = event;
    if (!over) return;
    const dragData = active.data?.current;
    if (dragData?.type === "schema-column") {
      // Insert at the column the cursor was over, not always at the end.
      // If over is null (dropped on empty canvas) we fall back to appending.
      const insertBeforeId =
        over && String(over.id) !== "simple-report-canvas"
          ? String(over.id)
          : undefined;
      addColumn(dragData.col as SchemaColumn, insertBeforeId);
      return;
    }
    if (active.id !== over.id) {
      reorderColumns(String(active.id), String(over.id));
    }
  }

  // On mount: fetch schema from backend, and load existing report if id is present
  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    async function init() {
      try {
        // Always fetch live schema
        const schema = await fetchReportSchema();
        if (!cancelled && schema?.length) setSchema(schema);
      } catch {
        // Fall back to MOCK_SCHEMA already in store — no-op
      }

      if (id) {
        try {
          const report = await getCustomReport(id);
          if (!cancelled) {
            loadReport(report.config);
            setIsReadOnly(!report.canEdit);
          }
        } catch {
          // Report not found or access denied — start fresh
          if (!cancelled) {
            resetReport();
            setIsReadOnly(false);
          }
        }
      } else if (!cancelled) {
        resetReport();
        setIsReadOnly(false);
      }

      if (!cancelled) setLoading(false);
    }

    init();
    return () => {
      cancelled = true;
    };
  }, [id]); // eslint-disable-line

  // Keyboard shortcuts: Ctrl+Z undo, Ctrl+Y / Ctrl+Shift+Z redo
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      const ctrl = e.ctrlKey || e.metaKey;
      if (!ctrl) return;
      if (e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if (e.key === "y" || (e.key === "z" && e.shiftKey)) {
        e.preventDefault();
        redo();
      }
    }
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [undo, redo]);

  // A blank report after ten seconds with no meaningful interaction is a useful
  // signal that someone may need orientation. Scrolling and pointer movement do
  // not reset this timer, so browsing the page is not treated as active work.
  useEffect(() => {
    if (!isBlankReport || hasDismissedContextualHelp) {
      setShowContextualHelp(false);
      return () => undefined;
    }

    const timer = window.setTimeout(() => {
      setShowContextualHelp(true);
    }, 10_000);

    return () => window.clearTimeout(timer);
  }, [hasDismissedContextualHelp, helpActivityVersion, isBlankReport]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-secondaryText">Loading report builder…</p>
        </div>
      </div>
    );
  }

  if (showStarters && !id) {
    return (
      <StarterScreen
        schema={schema}
        onSelect={(starter) => {
          loadReport(createStarterConfig(starter, schema));
          setShowStarters(false);
        }}
        onStartBlank={() => {
          resetReport();
          setShowStarters(false);
        }}
      />
    );
  }

  return (
    // A single DndContext wraps the entire builder so SchemaBrowser (useDraggable)
    // and ReportCanvas (useSortable) share the same drag session.
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div
        className="flex flex-col h-screen overflow-hidden bg-gray-50"
        onPointerDownCapture={markMeaningfulActivity}
        onKeyDownCapture={markMeaningfulActivity}
      >
        {/* Top header bar */}
        <ReportHeader readOnly={isReadOnly} />

        {isReadOnly && (
          <div className="flex items-center gap-2 px-4 py-2 text-xs text-blue-700 bg-blue-50 border-b border-blue-100">
            <Icon icon="mdi:eye-outline" width={15} />
            This team report is shared with you for viewing and download. Only
            its owner can edit it.
          </div>
        )}

        {/* Sheet tabs + help button */}
        <div
          className={`relative flex items-stretch flex-shrink-0 ${
            isReadOnly ? "pointer-events-none opacity-60" : ""
          }`}
        >
          <div className="flex-1 min-w-0">
            <SheetTabs />
          </div>
          <div className="relative flex-shrink-0 border-b border-borderColor">
            <button
              onClick={() => setShowHelp((v) => !v)}
              title="How to use the Custom Report Builder"
              className={`h-full flex items-center gap-1.5 px-3 text-xs font-medium transition-colors border-l border-borderColor ${
                showHelp
                  ? "bg-activeColor text-primary"
                  : "text-secondaryText hover:text-primary hover:bg-tertiaryBg"
              }`}
            >
              <Icon icon="mdi:help-circle-outline" width={15} />
              Help
            </button>
            {showHelp && (
              <HelpPanel
                onClose={() => setShowHelp(false)}
                onStartTour={startTour}
              />
            )}
          </div>
        </div>

        {/* Body: schema | center | editor */}
        <div
          className={`flex flex-1 min-h-0 ${
            isReadOnly ? "pointer-events-none opacity-60" : ""
          }`}
        >
          <SchemaBrowser />
          <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
            <FiltersPanel />
            <ReportCanvas isDragOver={isDragOver} />
          </div>
          <ColumnEditor />
        </div>

        {showContextualHelp && (
          <ContextualHelpPrompt
            onDismiss={dismissContextualHelp}
            onStartTour={startTour}
          />
        )}
      </div>

      {/* DragOverlay — renders outside the scrollable area, follows the cursor */}
      <DragOverlay dropAnimation={null}>
        {activeDragCol ? (
          // Canvas column card being reordered
          isAdvancedMode ? (
            <div className="w-44 rotate-2 scale-105 shadow-xl">
              <ColumnCard col={activeDragCol} allColumnIds={allColumnIds} />
            </div>
          ) : (
            <div className="max-w-lg rotate-1 shadow-lg opacity-90">
              <SimpleColumnRow col={activeDragCol} />
            </div>
          )
        ) : activeDragData?.type === "schema-column" ? (
          // Schema column being dragged onto the canvas
          <div className="bg-white border-2 border-primary rounded-xl px-3 py-2 shadow-xl text-xs font-semibold text-gray-800 max-w-[176px] flex items-center gap-1.5">
            <span className="text-primary">⊞</span>
            {activeDragData.col.displayName}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

export default CustomReportBuilder;
